import { useState } from "react";
import type { Business } from "@cliqsuite/db";
import { Field, Modal } from "./Modal";

const ERROR_LABELS: Record<string, string> = {
  name_mismatch: "That doesn't match the business name. Try again.",
  not_found: "Business not found. It may already be deleted.",
  forbidden: "Not authorized.",
  network: "Couldn't reach the server. Try again.",
};

export function DeleteBusinessModal({
  business,
  onClose,
  onConfirm,
}: {
  business: Business;
  onClose: () => void;
  onConfirm: (confirmName: string) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [confirmName, setConfirmName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = confirmName.trim() === business.name;

  const submit = async () => {
    if (!matches || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await onConfirm(confirmName.trim());
    if (!result.ok) {
      setError(ERROR_LABELS[result.error ?? ""] ?? "Couldn't delete. Try again.");
      setSubmitting(false);
      return;
    }
    onClose();
  };

  return (
    <Modal
      onClose={onClose}
      width={420}
      title={`Delete ${business.name}?`}
      footer={
        <>
          <button type="button" onClick={onClose} className="o-btn o-btn--grey" style={{ flex: 1 }}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!matches || submitting} className="o-btn o-btn--coral" style={{ flex: 1 }}>
            {submitting ? "Deleting…" : "Delete permanently"}
          </button>
        </>
      }
    >
      <div className="o-note" style={{ margin: "0 0 16px", background: "#FFF1F4", color: "#B81F39" }}>
        <span className="ms" aria-hidden="true">delete_forever</span>
        <span>Wipes the business and everything in it: owner login, stores, sales, customers, staff. This cannot be undone.</span>
      </div>
      <Field label="Type the business name to confirm">
        <input
          className="o-input"
          aria-label="Type the business name to confirm"
          value={confirmName}
          onChange={(e) => {
            setConfirmName(e.target.value);
            setError(null);
          }}
          placeholder={business.name}
          autoFocus
        />
      </Field>
      {error && <div className="o-error" style={{ marginTop: 0 }}>{error}</div>}
    </Modal>
  );
}
